import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { isValidPhone } from "@njbsoft/utils";
import { useTester, useUpdateTester } from "@/hooks/use-tester";
import { type OrgTester } from "@/hooks/use-testers";
import { useTestKits } from "@/hooks/use-test-kits";
import { useInlineField } from "@/hooks/use-inline-field";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { usePermissions } from "@/hooks/use-permissions";
import { useCustomFieldDefinitions } from "@/hooks/use-custom-field-definitions";
import { usePatchTesterCustomFields } from "@/hooks/use-tester-custom-fields";
import { useAppStore } from "@/store";
import { TesterCredentialsCard } from "@/components/testers/tester-credentials-card";
import { PendingTesterInvites } from "@/components/testers/pending-tester-invites";
import { TesterStatusControl } from "@/components/testers/tester-status-control";
import { TesterKitsTab } from "@/components/testers/tester-kits-tab";
import { testerStatusVariants } from "@/components/testers/testers-columns";
import { CredentialExpiryBadge } from "@/components/field-credentials/credential-expiry-badge";
import { EntityDocuments } from "@/components/documents/entity-documents";
import { CustomFieldsCard } from "@/components/custom-fields/custom-fields-card";
import { NotesCard } from "@/components/entity-notes/notes-card";
import {
  PageHeader,
  Breadcrumbs,
  Card,
  Tabs,
  Badge,
  PageSkeleton,
  EmptyValue,
  InlineEditable,
  InlineTextEditor,
  InlineBlockEditor,
  InlineDateEditor,
  type TabDef,
} from "@/components/ui";
import { type ReactNode } from "react";

type OrgTesterUpdate = components["schemas"]["OrgTesterUpdate"];

type TesterTab = "overview" | "company-history" | "kits" | "credentials" | "documents";

const ALLOWED_TABS: TesterTab[] = [
  "overview",
  "company-history",
  "kits",
  "credentials",
  "documents",
];

interface TesterDetailSearch {
  tab?: TesterTab;
}

function parseTab(value: unknown): TesterTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

export const Route = createFileRoute("/_authenticated/testers/$id/")({
  component: TesterDetailPage,
  validateSearch: (s: Record<string, unknown>): TesterDetailSearch => ({
    tab: parseTab(s.tab),
  }),
});

function joinTesterAddress(tester: OrgTester): string | null {
  const cityState = [tester.city, tester.state].filter(Boolean).join(", ");
  const lastLine = [cityState, tester.postal_code].filter(Boolean).join(" ");
  const lines = [tester.address_line1, tester.address_line2, lastLine].filter((l): l is string =>
    Boolean(l && l.trim()),
  );
  return lines.length ? lines.join("\n") : null;
}

/** The five address fields as the block editor's initial value map. */
function addressFieldValues(tester: OrgTester): Record<string, string> {
  return {
    address_line1: tester.address_line1 ?? "",
    address_line2: tester.address_line2 ?? "",
    city: tester.city ?? "",
    state: tester.state ?? "",
    postal_code: tester.postal_code ?? "",
  };
}

// Same rendering as the testers list's expiry column so header chip, list
// chip, and Overview field never disagree on format.
function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Tester detail — keyed on the org_testers row id (the authorization, NOT
 * the profile), so re-authorization history stays navigable. Five tabs:
 * Overview (identity card split tester-managed read-only / org-managed
 * inline-editable + the authorization card as inline-editable dl fields +
 * pending invites), Company history (the company the tester belongs to
 * today, derived from their live company membership), Kits (the tester's
 * gauges), Credentials, and Documents. FSM verbs ride the header status
 * control; identity name/email rows stay tester-owned and read-only.
 */
function TesterDetailPage() {
  const { t } = useTranslation(["testers", "common", "documents", "field-credentials"]);
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const { can } = usePermissions();
  const orgId = useAppStore((s) => s.activeOrgId);
  const canUpdate = can("testers", "update");

  const { data: tester, isLoading, isError } = useTester(id);
  useRecentPageTitle(tester?.full_name ?? undefined);

  // Count-only probe for the Kits pill (single row / shared cache, server
  // total). Company history, Credentials and Documents get NO pill — no cheap
  // count exists there today, and none is added for a pill.
  const kitsCountQuery = useTestKits({
    owner_tester_profile_id: tester?.profile_id,
    limit: 1,
  });

  const customFieldDefinitions = useCustomFieldDefinitions("org_tester");
  const { patchValues: patchCustomFields } = usePatchTesterCustomFields(id);

  const updateMutation = useUpdateTester(id);
  const patchField = useInlineField<OrgTesterUpdate>((body) => updateMutation.mutateAsync(body), {
    successKey: "testers:edit.success",
    errorKeyByCode: {
      authorization_invalid_transition: "testers:errors.invalidTransition",
      tester_authorization_terminal: "testers:errors.authorizationTerminal",
    },
  });

  // Re-validated at read time, not just in validateSearch, so an unknown tab in
  // a hand-typed or stale URL still lands on the default body.
  const activeTab: TesterTab = parseTab(tab) ?? "overview";
  const setActiveTab = (next: TesterTab) => {
    void navigate({
      to: "/testers/$id",
      params: { id },
      search: next === "overview" ? {} : { tab: next },
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !tester) {
    return (
      <div className="p-8">
        <Breadcrumbs items={[{ label: t("list.title"), to: "/testers" }]} />
        <PageHeader title={t("errors.loadDetailFailed")} />
      </div>
    );
  }

  // Mirrors the server's terminal guard: a revoked authorization refuses every
  // write with the typed 409, so the card never offers an edit the API rejects
  // — suspended stays editable, only revoked is terminal.
  const canEditCustomFields = canUpdate && tester.status !== "revoked";

  const tabs: TabDef<TesterTab>[] = [
    { value: "overview", label: t("detail.tabs.overview") },
    { value: "company-history", label: t("detail.tabs.companyHistory") },
    {
      value: "kits",
      label: t("detail.tabs.kits"),
      count: kitsCountQuery.data?.total_count,
    },
    { value: "credentials", label: t("detail.tabs.credentials") },
    { value: "documents", label: t("documents:page.title") },
  ];

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[{ label: t("list.title"), to: "/testers" }, { label: tester.full_name }]}
      />
      <PageHeader
        title={tester.full_name}
        meta={
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={testerStatusVariants[tester.status]}>
              {t(`status.${tester.status}`)}
            </Badge>
            {tester.testing_company_name && (
              <span className="text-sm text-muted">{tester.testing_company_name}</span>
            )}
            {tester.authorization_expires_at && (
              <span className="inline-flex items-center gap-2 text-sm text-muted">
                {formatDate(tester.authorization_expires_at)}
                <CredentialExpiryBadge expiresAt={tester.authorization_expires_at} />
              </span>
            )}
          </div>
        }
        actions={<TesterStatusControl tester={tester} />}
      />

      <div className="mt-4">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={tester.full_name}
        />
        <div className="mt-6" role="tabpanel">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <Card title={t("detail.identityTitle")}>
                <div className="space-y-6">
                  <section>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-muted">
                      {t("detail.testerManagedSection")}
                    </h4>
                    <dl className="mt-2 space-y-2 text-sm">
                      <div>
                        <dt className="text-muted">{t("fields.fullName")}</dt>
                        <dd className="flex flex-wrap items-center gap-2">
                          <span className="text-muted">{tester.full_name}</span>
                          <Badge variant="default">{t("detail.managedByTesterChip")}</Badge>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted">{t("fields.email")}</dt>
                        <dd className="flex flex-wrap items-center gap-2">
                          <span className="text-muted">{tester.email}</span>
                          <Badge variant="default">{t("detail.managedByTesterChip")}</Badge>
                        </dd>
                      </div>
                    </dl>
                  </section>
                  <section>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-muted">
                      {t("detail.orgManagedSection")}
                    </h4>
                    <dl className="mt-2 space-y-2 text-sm">
                      <div>
                        <dt className="text-muted">{t("fields.title")}</dt>
                        <dd className="text-on-surface">
                          <InlineEditable
                            label={t("fields.title")}
                            disabled={!canUpdate}
                            display={tester.title ?? <EmptyValue />}
                          >
                            {(close) => (
                              <InlineTextEditor
                                ariaLabel={t("fields.title")}
                                initial={tester.title ?? ""}
                                close={close}
                                onSubmit={(v) => void patchField({ title: v === "" ? null : v })}
                              />
                            )}
                          </InlineEditable>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted">{t("fields.phone")}</dt>
                        <dd className="text-on-surface">
                          <InlineEditable
                            label={t("fields.phone")}
                            disabled={!canUpdate}
                            display={tester.phone ?? <EmptyValue />}
                          >
                            {(close) => (
                              <InlineTextEditor
                                ariaLabel={t("fields.phone")}
                                initial={tester.phone ?? ""}
                                close={close}
                                onSubmit={(v) => void patchField({ phone: v === "" ? null : v })}
                                // isValidPhone("") is TRUE by design (phone is
                                // optional) -- that is what lets an emptied
                                // field commit its clear instead of being
                                // blocked as invalid.
                                validate={(next) =>
                                  isValidPhone(next)
                                    ? undefined
                                    : t("common:validation.invalidPhone")
                                }
                              />
                            )}
                          </InlineEditable>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted">{t("fields.address")}</dt>
                        <dd className="text-on-surface">
                          <InlineEditable
                            label={t("fields.address")}
                            disabled={!canUpdate}
                            display={
                              <span className="whitespace-pre-line">
                                {joinTesterAddress(tester) ?? <EmptyValue />}
                              </span>
                            }
                          >
                            {(close) => (
                              <InlineBlockEditor
                                initial={addressFieldValues(tester)}
                                fields={[
                                  { key: "address_line1", label: t("fields.addressLine1") },
                                  { key: "address_line2", label: t("fields.addressLine2") },
                                  { key: "city", label: t("fields.city") },
                                  { key: "state", label: t("fields.state") },
                                  { key: "postal_code", label: t("fields.postalCode") },
                                ]}
                                close={close}
                                onSubmit={(next) =>
                                  void patchField({
                                    address_line1:
                                      next.address_line1 === "" ? null : next.address_line1,
                                    address_line2:
                                      next.address_line2 === "" ? null : next.address_line2,
                                    city: next.city === "" ? null : next.city,
                                    state: next.state === "" ? null : next.state,
                                    postal_code: next.postal_code === "" ? null : next.postal_code,
                                  })
                                }
                              />
                            )}
                          </InlineEditable>
                        </dd>
                      </div>
                    </dl>
                  </section>
                </div>
              </Card>

              <Card title={t("detail.authorizationTitle")}>
                {tester.status === "revoked" && (
                  <div className="mb-4 rounded-md bg-gray-100 p-4 text-sm text-muted dark:bg-gray-700 dark:text-gray-300">
                    {t("detail.terminalBanner")}
                  </div>
                )}
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <Field label={t("fields.company")}>
                    <span className="flex flex-wrap items-center gap-2">
                      <CompanyLink
                        companyId={tester.testing_company_id}
                        companyName={tester.testing_company_name}
                      />
                      <Badge variant="default">{t("detail.readOnlyChip")}</Badge>
                    </span>
                  </Field>
                  <Field label={t("fields.authorizationExpiresAt")}>
                    <InlineEditable
                      label={t("fields.authorizationExpiresAt")}
                      disabled={!canUpdate}
                      display={
                        tester.authorization_expires_at ? (
                          <span className="inline-flex items-center gap-2">
                            {formatDate(tester.authorization_expires_at)}
                            <CredentialExpiryBadge expiresAt={tester.authorization_expires_at} />
                          </span>
                        ) : (
                          <EmptyValue />
                        )
                      }
                    >
                      {(close) => (
                        <InlineDateEditor
                          ariaLabel={t("fields.authorizationExpiresAt")}
                          initial={tester.authorization_expires_at ?? ""}
                          close={close}
                          onSubmit={(v) =>
                            void patchField({
                              authorization_expires_at: v === "" ? null : v,
                            })
                          }
                        />
                      )}
                    </InlineEditable>
                  </Field>
                  <Field label={t("fields.authorizedAt")}>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-muted">
                        {tester.authorized_at ? formatDate(tester.authorized_at) : <EmptyValue />}
                      </span>
                      <Badge variant="default">{t("detail.readOnlyChip")}</Badge>
                    </span>
                  </Field>
                  {tester.suspension_reason && (
                    <Field label={t("fields.suspensionReason")}>
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="whitespace-pre-line text-muted">
                          {tester.suspension_reason}
                        </span>
                        <Badge variant="default">{t("detail.readOnlyChip")}</Badge>
                      </span>
                    </Field>
                  )}
                </dl>
              </Card>

              <PendingTesterInvites email={tester.email} />
              <div className="grid gap-4 lg:grid-cols-2">
                <CustomFieldsCard
                  definitions={customFieldDefinitions.data?.items ?? []}
                  values={tester.custom_fields}
                  canEdit={canEditCustomFields}
                  patchValues={patchCustomFields}
                />
              </div>
              {/* Internal notes close out the Overview column; the parent is
                  the org_testers row (this route's id), so note history
                  follows the authorization, not the person. */}
              <NotesCard parent="org_tester" parentId={tester.id} />
            </div>
          )}
          {activeTab === "company-history" && (
            <Card title={t("detail.currentCompanyTitle")}>
              <CompanyLink
                companyId={tester.testing_company_id}
                companyName={tester.testing_company_name}
                emptyLabel={t("detail.noCurrentCompany")}
              />
            </Card>
          )}
          {activeTab === "kits" && <TesterKitsTab tester={tester} />}
          {activeTab === "credentials" && <TesterCredentialsCard testerId={tester.id} />}
          {activeTab === "documents" && orgId && (
            <div>
              <EntityDocuments entityType="org_tester" entityId={tester.id} orgId={orgId} />
              <p className="mt-2 text-xs text-muted">
                {t("field-credentials:submittedEvidence.documentsProvenance")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The tester's company, derived from their live company membership — the
 * utility never sets it, so it renders as a link to the company record (or
 * the empty marker) and never as an editor.
 */
function CompanyLink({
  companyId,
  companyName,
  emptyLabel,
}: {
  companyId?: string | null;
  companyName?: string | null;
  emptyLabel?: string;
}) {
  if (!companyId || !companyName) {
    return emptyLabel ? <span className="text-sm text-muted">{emptyLabel}</span> : <EmptyValue />;
  }
  return (
    <Link
      to="/testing-companies/$id"
      params={{ id: companyId }}
      className="text-sm text-primary hover:underline"
    >
      {companyName}
    </Link>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-muted">{label}</dt>
      <dd className="mt-1 text-sm text-on-surface">{children}</dd>
    </div>
  );
}
